import { findIndex } from "common-utils/utils/array";
import {
  GalleryAlbum,
  GalleryAlbumUrlHandleString,
  GalleryCollector,
  ObjectIdAsHexString,
} from "kreate-common/modules/business-types";
import { httpPost$CreateGalleryAlbum } from "kreate-server/commands/CreateGalleryAlbum/fetcher";
import { httpPost$DeleteGalleryAlbum } from "kreate-server/commands/DeleteGalleryAlbum/fetcher";
import { httpPost$UpdateGalleryAlbum } from "kreate-server/commands/UpdateGalleryAlbum/fetcher";
import { useRouter } from "next/router";
import React from "react";

import { useLoginStatus } from "@/modules/common-hooks/useLoginStatus";
import { useWithErrorHandler } from "@/modules/common-hooks/useWithErrorHandler";
import { shamelesslyRevalidateEverything } from "@/modules/common-utils/shamelesslyRevalidateEverything";
import { EM_DASH } from "@/modules/common-utils/unicode";
import { InlineError } from "@/modules/form-field/errors";
import { useToast } from "@/modules/kreate-contexts/ToastContext";
import { useModalTrigger$ModalConfirm } from "@/modules/kreate-modals/modals/ModalConfirm/hooks";
import { useModalTrigger$ModalFailure } from "@/modules/kreate-modals/modals/ModalFailure/hooks";
import { useModalTrigger$ModalPrompt } from "@/modules/kreate-modals/modals/ModalPrompt/hooks";
import { getLinkToPageGalleryCollector$Album } from "@/modules/navigation/utils/getLinkTo";
import {
  getPriorityIfBringForward,
  getPriorityIfBringToFront,
  getPriorityIfInsertAfter,
  getPriorityIfInsertBefore,
  getPriorityIfMoveXToY,
  getPriorityIfSendBackward,
  getPriorityIfSendToBack,
  getPriorityList,
} from "@/modules/priorities/utils";
import { useGalleryAlbumList } from "@/modules/resources/hooks/useGalleryAlbumList";

type SidebarLocation =
  | { type: "all-assets" }
  | { type: "hidden-assets" }
  | { type: "unsorted-assets" }
  | { type: "for-sale" }
  | { type: "albums"; galleryAlbumId: ObjectIdAsHexString }
  | { type: "sent-offers" }
  | { type: "received-offers" };

type Params = {
  galleryCollector: GalleryCollector | null | undefined;
  sidebarLocation: SidebarLocation;
  addressIsh: string;
};

export function useGallerySidebarApi(params: Params) {
  const { galleryCollector, sidebarLocation, addressIsh } = params;
  const router = useRouter();
  const loginStatus = useLoginStatus();
  const { showMessage } = useToast();
  const [draggedAlbum, setDraggedAlbum] = React.useState<GalleryAlbum>();

  const modalTriggers = {
    ModalPrompt: useModalTrigger$ModalPrompt(),
    ModalFailure: useModalTrigger$ModalFailure(),
    ModalConfirm: useModalTrigger$ModalConfirm(),
  };

  const { busy, withErrorHandler } = useWithErrorHandler(
    async (error) => await modalTriggers.ModalFailure.openModalWithError(error)
  );

  const userProfile = galleryCollector?.populated?.userProfile;

  const [galleryAlbumList$Data] = useGalleryAlbumList(
    userProfile ? { createdBy: userProfile?.userId } : undefined
  );

  const isLoggedInUserGalleryOwner =
    loginStatus?.loggedIn &&
    loginStatus.userId === galleryCollector?.populated?.userProfile?.userId;

  const createAlbum =
    userProfile && isLoggedInUserGalleryOwner
      ? withErrorHandler(async () => {
          const albumTitle = await modalTriggers.ModalPrompt.openModal(
            "Exhibit name"
          );
          await httpPost$CreateGalleryAlbum(
            "/api/v1/all-gallery-albums/create",
            { createdBy: userProfile.userId, title: albumTitle }
          );

          showMessage({ color: "success", title: "Created exhibit" });
          await shamelesslyRevalidateEverything();
        })
      : undefined;

  const updateAlbumTitle =
    userProfile && isLoggedInUserGalleryOwner
      ? withErrorHandler(async (album: GalleryAlbum) => {
          const newAlbumTitle = await modalTriggers.ModalPrompt.openModal(
            "Exhibit name",
            {
              initialValue: album.title,
              validate: (value) => {
                InlineError.assert$PunishAfterEditing(
                  value,
                  "Exhibit name must not be blank"
                );
              },
            }
          );

          await httpPost$UpdateGalleryAlbum(
            `/api/v1/gallery-album/${album.galleryAlbumId}/edit`,
            { title: newAlbumTitle }
          );

          showMessage({ color: "success", title: "Updated exhibit" });
          await shamelesslyRevalidateEverything();
        })
      : undefined;

  const updateAlbumHandle =
    userProfile && isLoggedInUserGalleryOwner
      ? withErrorHandler(async (album: GalleryAlbum) => {
          const newAlbumHandle = await modalTriggers.ModalPrompt.openModal(
            "Exhibit handle",
            {
              initialValue: album.handle || undefined,
              validate: validateAlbumHandle,
            }
          );

          await httpPost$UpdateGalleryAlbum(
            `/api/v1/gallery-album/${album.galleryAlbumId}/edit`,
            { handle: newAlbumHandle || null }
          );

          showMessage({ color: "success", title: "Updated exhibit" });
          await shamelesslyRevalidateEverything();
          if (
            addressIsh &&
            sidebarLocation.type === "albums" &&
            sidebarLocation.galleryAlbumId === album.galleryAlbumId
          ) {
            await router.replace(
              getLinkToPageGalleryCollector$Album(addressIsh, newAlbumHandle),
              undefined,
              { scroll: false }
            );
          }
        })
      : undefined;

  const deleteAlbum =
    userProfile && isLoggedInUserGalleryOwner
      ? withErrorHandler(async (album: GalleryAlbum) => {
          const confirmed = await modalTriggers.ModalConfirm.openModal(
            `Are you sure you want to remove Exhibit "${
              album.title || EM_DASH
            }"`
          );

          if (!confirmed) return;

          await httpPost$DeleteGalleryAlbum(
            `/api/v1/gallery-album/${album.galleryAlbumId}/delete`,
            {}
          );

          showMessage({ color: "success", title: "Removed exhibit" });
          await shamelesslyRevalidateEverything();
        })
      : undefined;

  const setAlbumPriority = isLoggedInUserGalleryOwner
    ? withErrorHandler(async (album: GalleryAlbum, priority: number) => {
        await httpPost$UpdateGalleryAlbum(
          `/api/v1/gallery-album/${album.galleryAlbumId}/edit`,
          { priority }
        );

        showMessage({ color: "success", title: "Reordered exhibit" });
        await shamelesslyRevalidateEverything();
      })
    : undefined;

  const galleryAlbums = galleryAlbumList$Data?.galleryAlbums;

  const priorities = getPriorityList({
    items: galleryAlbums || [],
    // TODO: @kien-kreate: update in case of infinite-scrolling
    exhausted: true,
    toPriority: (item) => item.priority,
  });

  const indexOf = (galleryAlbum: GalleryAlbum | null | undefined) => {
    if (!galleryAlbum) return undefined;
    return findIndex(galleryAlbums, galleryAlbum, (a) => a.galleryAlbumId);
  };

  const getReorderingInfo = (galleryAlbum: GalleryAlbum) => {
    const index = indexOf(galleryAlbum);
    if (index == null) return undefined;

    return {
      priorityIfBringToFront: getPriorityIfBringToFront(priorities, index),
      priorityIfBringForward: getPriorityIfBringForward(priorities, index),
      priorityIfInsertBefore: getPriorityIfInsertBefore(priorities, index),
      priorityIfInsertAfter: getPriorityIfInsertAfter(priorities, index),
      priorityIfSendBackward: getPriorityIfSendBackward(priorities, index),
      priorityIfSendToBack: getPriorityIfSendToBack(priorities, index),
      priorityIfDropHere: getPriorityIfMoveXToY(
        priorities,
        indexOf(draggedAlbum),
        index
      ),
    };
  };

  return {
    busy,
    createAlbum,
    updateAlbumTitle,
    updateAlbumHandle,
    deleteAlbum,
    galleryAlbums,
    isLoggedInUserGalleryOwner,
    getReorderingInfo,
    setAlbumPriority,
    draggedAlbum: {
      value: draggedAlbum,
      setValue: setDraggedAlbum,
    },
    params,
  };
}

function validateAlbumHandle(draft: string) {
  if (draft === "") return;
  // while editing
  InlineError.assert$PunishWhileEditing(
    /^[0-9A-Za-z_-]*$/.test(draft),
    "Must only contains letters, numbers, hyphens, underscores"
  );
  InlineError.assert$PunishWhileEditing(
    !draft || draft.length <= 23,
    "Must contains at most 23 characters"
  );
  InlineError.assert$PunishWhileEditing(
    !draft.startsWith("-") && !draft.startsWith("_"),
    "Must not start with hyphens or underscores"
  );

  // after editing
  InlineError.assert$PunishAfterEditing(
    !draft || draft.length >= 3,
    "Must contains at least 3 characters"
  );
  InlineError.assert$PunishAfterEditing(
    !draft.endsWith("-") && !draft.endsWith("_"),
    "Must not end with hyphens or underscores"
  );
  InlineError.assert$PunishAfterEditing(
    !draft || GalleryAlbumUrlHandleString.safeParse(draft).success,
    "Invalid handle"
  );
}
